/**
 * Единая карта продукта — `AgroMap` (он же `MapWidget`), agroai-6al.1.
 *
 * Протокол использования — `docs/rules/map-widget.md`. Коротко: страница
 * передаёт слой (`colorBy`), значения слоя по id контура, начальные границы и
 * набор функций; всё остальное — одинаково на каждом экране: легенда по факту
 * данных, переключатель подложки, фильтр слоя по легенде, разворот на весь
 * экран, ползунок даты, точки и кластеры статуса на отдалении, граница района
 * (гало и маска полотна) и общая карточка контура по нажатию.
 *
 * Движок один — канонический `features/map/MapCanvas` (решение 9 плана
 * `astra-on-canon`); слои языка Astra — `features/map/astraStyle.ts`, слои
 * данных как конфигурации — `layers.ts`, заливки и легенды — `paint.ts`.
 * Своих цветов у страниц нет: значение слоя переводится в цвет только здесь,
 * из зеркала токенов (ADR-0009).
 *
 * Чего кадр не делает: подписей на полотне (нет глифов — нет шрифтов из
 * сети); имена живут в подсказке и в карточке контура. `massifLabels`
 * принимается ради совместимости экранов и не рисуется.
 */

import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import type { FeatureCollection } from 'geojson';
import { Maximize2, Minimize2, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { MapCanvas, type AstraFeatureProps } from '../../features/map/MapCanvas';
import { geoBounds, type Bbox } from '../../features/map/mapModel';
import { resolveFitBbox } from './fit';
import { TIER_REGISTRY } from '../../features/map/tierRegistry';
import { useAnalysisContext } from '../../analysis/context';
import { EmptyState } from '../ui/EmptyState';
import { EvidenceMark } from '../ui/EvidenceMark';
import { Icon } from '../ui/Icon';
import { MapLayerNotice, MapLegend } from './MapLegend';
import { LayersPanel } from './LayersPanel';
import { MapErrorBoundary } from './MapErrorBoundary';
import { ContourCard, type ContourCardData } from './ContourCard';
import { MapTimeline, type MapTimelineProps } from './MapTimeline';
import {
  buildPaint,
  COLOR_BY_LABEL,
  LEGEND_NONE,
  SUPPRESSED_FILL,
  type ColorBy,
  type LegendEntry,
  type WorkStatusValue,
} from './paint';
import {
  layerHasData,
  layerSpec,
  presentValues,
  SEVERITY_COLOR,
  statusPoints,
  visibleIds,
  wantsPoint,
  type LayerFeatureProps,
  type MapLayerData,
  type PointsMode,
} from './layers';
import { useContourGeo } from './useContourGeo';
import { PALETTE } from '../styles/palette';
import { useTrackData } from '../data/useTrackData';
import { FILES } from '../data/loader';
import { haUnit } from '../lib/format';
import type { ContourMeta, Crop, Farm, LngLat, Massif, QuarantineStatusValue } from '../data/types';
import './map.css';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  tier: {
    id: 'map.widget.tier',
    defaultMessage: 'Ярус: {label}',
    description: 'Кадр карты: подпись образца обводки в легенде слоя «Геометрия»; {label} — название яруса связи, показанное из реестра',
  },
  noLayerData: {
    id: 'map.widget.noLayerData',
    defaultMessage: 'данные слоя «{title}» на этот экран не переданы — раскрашивать нечем',
    description: 'Кадр карты: причина, почему слой не показан (строчная фраза в пояснении вместо легенды); {title} — название слоя',
  },
  tipFarmNo: {
    id: 'map.widget.tipFarmNo',
    defaultMessage: 'Хозяйство {id}',
    description: 'Кадр карты: всплывающая подсказка над контуром: название хозяйства, когда имя неизвестно; {id} — номер хозяйства',
  },
  tipNoFarm: {
    id: 'map.widget.tipNoFarm',
    defaultMessage: 'Хозяйство не сопоставлено',
    description: 'Кадр карты: всплывающая подсказка над контуром: контур не сопоставлен ни с одним хозяйством',
  },
  tipMassifNo: {
    id: 'map.widget.tipMassifNo',
    defaultMessage: 'Массив {id}',
    description: 'Кадр карты: всплывающая подсказка над контуром: название массива, когда имя неизвестно; {id} — номер массива',
  },
  tipNo: {
    id: 'map.widget.tipNo',
    defaultMessage: '№ {no}',
    description: 'Кадр карты: всплывающая подсказка над контуром: номер контура; {no} — номер',
  },
  missingTitle: {
    id: 'map.widget.missingTitle',
    defaultMessage: 'Карта пока не собирается',
    description: 'Кадр карты: заголовок пустого состояния на месте карты, когда не загрузилась геометрия',
  },
  geoNoReason: {
    id: 'map.widget.geoNoReason',
    defaultMessage: 'геометрия не загрузилась',
    description: 'Кадр карты: причина по умолчанию в пояснении «Карта пока не собирается» (строчная фраза)',
  },
  missingApi: {
    id: 'map.widget.missingApi',
    defaultMessage: '{reason}. Остальная страница читает те же данные и работает; обновление страницы повторит запрос.',
    description: 'Кадр карты: пояснение пустого состояния карты при ошибке сервера; {reason} — причина без точки',
  },
  missingFixtures: {
    id: 'map.widget.missingFixtures',
    defaultMessage: '{reason}. Геометрия приходит отдельным шагом подготовки данных: как только она появится, кадр оживёт сам.',
    description: 'Кадр карты: пояснение пустого состояния карты на демонстрационных данных; {reason} — причина без точки',
  },
  watchShow: {
    id: 'map.widget.watchShow',
    defaultMessage: '{count, plural, one {# красный и жёлтый контур} few {# красных и жёлтых контура} many {# красных и жёлтых контуров} other {# красных и жёлтых контура}} — показать',
    description: 'Кадр карты: кнопка в легенде слоя «Карантин»: показать красные и жёлтые контуры; {count} — число контуров',
  },
  watchShowOnMap: {
    id: 'map.widget.watchShowOnMap',
    defaultMessage: '{count, plural, one {# красный и жёлтый контур} few {# красных и жёлтых контура} many {# красных и жёлтых контуров} other {# красных и жёлтых контура}} (на карте {onMap}) — показать',
    description: 'Кадр карты: кнопка в легенде слоя «Карантин», когда часть контуров ещё не нарисована; {count} — всего, {onMap} — сколько уже на карте',
  },
  attentionShow: {
    id: 'map.widget.attentionShow',
    defaultMessage: '{count, plural, one {# контур требует} few {# контура требуют} many {# контуров требуют} other {# контура требуют}} внимания — показать',
    description: 'Кадр карты: кнопка в легенде слоя: показать контуры, требующие внимания; {count} — число контуров',
  },
  hiddenCount: {
    id: 'map.widget.hiddenCount',
    defaultMessage: 'Скрыто значений слоя: {count}.',
    description: 'Кадр карты: строка над легендой: сколько значений слоя скрыто фильтром; {count} — число',
  },
  showAll: {
    id: 'map.widget.showAll',
    defaultMessage: 'Показать все',
    description: 'Кадр карты: кнопка в строке «Скрыто значений слоя»: снять фильтр; коротко',
  },
  loadingGeo: {
    id: 'map.widget.loadingGeo',
    defaultMessage: 'Геометрия района загружается…',
    description: 'Кадр карты: надпись на заглушке, пока грузятся границы района',
  },
  buildingFrame: {
    id: 'map.widget.buildingFrame',
    defaultMessage: 'Карта собирает первый кадр…',
    description: 'Кадр карты: надпись на заглушке, пока карта рисует первый кадр',
  },
  unmatched: {
    id: 'map.widget.unmatched',
    defaultMessage: 'У {count, plural, one {# полигона} other {# полигонов}} нет строки в реестре контуров среза: их атрибуты неизвестны, и заливка у них — «нет данных», а не значение слоя.',
    description: 'Кадр карты: предупреждение над картой: есть полигоны без строки в реестре; {count} — число полигонов',
  },
  unmatchedReason: {
    id: 'map.widget.unmatchedReason',
    defaultMessage: 'У {count, plural, one {# полигона} other {# полигонов}} нет строки в реестре контуров среза ({reason}): их атрибуты неизвестны, и заливка у них — «нет данных», а не значение слоя.',
    description: 'Кадр карты: то же предупреждение с причиной; {count} — число полигонов, {reason} — причина без точки',
  },
  stubGeometry: {
    id: 'map.widget.stubGeometry',
    defaultMessage: 'Геометрия-заглушка',
    description: 'Кадр карты: метка «Демо» на панели карты: показана заглушечная геометрия, не настоящая',
  },
  collapse: {
    id: 'map.widget.collapse',
    defaultMessage: 'Свернуть карту',
    description: 'Кадр карты: aria-label кнопки на панели карты: вернуть карту к обычному размеру',
  },
  expand: {
    id: 'map.widget.expand',
    defaultMessage: 'Развернуть карту на весь экран',
    description: 'Кадр карты: aria-label кнопки на панели карты: развернуть на весь экран',
  },
  collapseTitle: {
    id: 'map.widget.collapseTitle',
    defaultMessage: 'Свернуть (Esc)',
    description: 'Кадр карты: всплывающая подсказка кнопки «Свернуть»; Esc — название клавиши',
  },
  expandTitle: {
    id: 'map.widget.expandTitle',
    defaultMessage: 'Развернуть на весь экран',
    description: 'Кадр карты: всплывающая подсказка кнопки «Развернуть»',
  },
  panelHide: {
    id: 'map.widget.panelHide',
    defaultMessage: 'Свернуть панель «{title}»',
    description: 'Кадр карты на весь экран: кнопка сворачивания боковой панели управления (список, по которому карта меняет данные); {title} — заголовок панели',
  },
  panelShow: {
    id: 'map.widget.panelShow',
    defaultMessage: 'Показать панель «{title}»',
    description: 'Кадр карты на весь экран: кнопка, раскрывающая свёрнутую боковую панель управления; {title} — заголовок панели',
  },
});


/** Резолверы имён: карта знает только идентификаторы из properties. */
export interface MapNameResolvers {
  farm?: (id: string) => string | null | undefined;
  massif?: (id: string) => string | null | undefined;
}

/**
 * Набор функций кадра. Не заданное берётся из умолчаний режима (`mode`):
 * у станции (`full`) включено всё, у кадра страницы (`frame`) — подложка и
 * разворот, у превью (`mini`) — ничего, кроме выбора контура.
 */
export interface MapTools {
  /** Переключатель подложки (снимок / подписи / без подложки). */
  basemap?: boolean;
  /** Кнопка «развернуть на весь экран». */
  fullscreen?: boolean;
  /** Фильтр слоя: позиции легенды скрывают и показывают контуры значения. */
  layerFilter?: boolean;
  /** Нажатие по контуру выбирает его (и открывает карточку). */
  select?: boolean;
}

export type MapMode = 'frame' | 'full' | 'mini';

/**
 * Панель управления карты (решение владельца 08.10, журнал №1390): внешний
 * элемент страницы, который задаёт карте данные и фильтры — список работ,
 * хозяйств, поливов. На странице он стоит рядом с картой как обычно; в
 * развёрнутом кадре кадр показывает его ВНУТРИ себя сворачиваемой боковой
 * панелью, и выбор в ней меняет данные карты без выхода из полноэкранного
 * режима. Состояние выбора живёт у страницы: панель — тот же интерфейс,
 * только компактнее (`content` страница рисует с флагом компактности).
 */
export interface MapPanel {
  /** Заголовок панели — как у блока на странице. */
  title: string;
  /** Одна строка под заголовком: что выбрано сейчас. */
  summary?: string;
  /** Тот же интерфейс выбора, что на странице, в компактном виде. */
  content: ReactNode;
}

/**
 * Где показывать общую карточку контура. `auto` — панель внутри кадра у
 * станции и развёрнутого кадра, боковая шторка у кадра страницы.
 */
export type MapCardMode = 'auto' | 'side' | 'drawer' | 'none';

export interface MapWidgetProps {
  /**
   * Слой раскраски. Легенда рисуется всегда: раскраска без легенды — цветные
   * пятна, поэтому в контракте нет выключателя легенды, есть только её место.
   */
  colorBy: ColorBy;
  /** Место легенды; `collapsed` — свёрнута до строки образцов поверх кадра. */
  legend?: 'inline' | 'panel' | 'below' | 'collapsed';
  /** Показывать только эти контуры; undefined — показывать все. */
  filterIds?: string[];
  /** Подогнать вид под эти контуры. */
  focusIds?: string[];
  /** Начальные границы, если фокуса нет; по умолчанию — район. */
  bounds?: Bbox;
  /** `frame` — кадр внутри страницы, `full` — станция, `mini` — превью без управления. */
  mode?: MapMode;
  /** Набор функций сверх умолчаний режима. */
  tools?: MapTools;
  /** Переключатель слоёв (нужен и `onColorByChange`). */
  layersPanel?: boolean;
  /** Какие слои предложить в переключателе. */
  layers?: ColorBy[];
  onColorByChange?: (next: ColorBy) => void;
  /** Уведомление о выборе контура; карточку кадр открывает сам (см. `card`). */
  onSelect?: (contour: ContourMeta | null) => void;
  selectedId?: string | null;
  /** Карточка контура по нажатию; по умолчанию `auto`. */
  card?: MapCardMode;
  /** Раздел страницы в общей карточке контура. */
  cardExtra?: (contour: ContourCardData) => ReactNode;
  /** Демо/синтетика: постоянная штриховка. */
  hatchIds?: string[];
  /**
   * Тёмная обводка отмеченных контуров — второй признак поверх заливки
   * (например, «проблема вернулась после устранения»). Смысл обводки страница
   * называет `ringLabel`: строка уходит в легенду.
   */
  ringIds?: string[];
  ringLabel?: string;
  /** Обводка пунктиром — другой признак, чем сплошная. */
  ringDashed?: boolean;
  /** Статус работы по контурам; значения совпадают с `Obligation.state`. */
  workStatusById?: Record<string, WorkStatusValue>;
  quarantineById?: Record<string, QuarantineStatusValue>;
  /** Значения прочих слоёв: вода, произвольный слой (`layers.ts`). */
  data?: MapLayerData;
  /** Точки статуса на отдалении; по умолчанию — правило слоя. */
  points?: PointsMode;
  /** Ползунок даты: меняет `as_of` данных слоя колбэком страницы. */
  timeline?: MapTimelineProps;
  crops?: Crop[];
  /** Принимается ради совместимости экранов; полотно подписей не рисует. */
  massifLabels?: Massif[];
  height?: number | string;
  /**
   * Кадр тянется на весь остаток родителя (flex-колонка). Высота не считается
   * магическим calc: её задаёт раскладка страницы.
   */
  fill?: boolean;
  /** Одна строка над картой: что именно показано. */
  caption?: string;
  /**
   * Слой выбран, а питать его нечем: причина словами вместо пустой легенды.
   * Заливка при этом нейтральная — цветных пятен без смысла не бывает.
   */
  layerUnavailable?: string | null;
  /** Имена вместо идентификаторов в подсказке и карточке. */
  resolveNames?: MapNameResolvers;
  /** Панель управления внутри развёрнутого кадра (см. `MapPanel`). */
  panel?: MapPanel;
}

/** Стабильная ссылка: литерал `[]` в пропсах пересобирал бы стиль на каждый рендер. */
const NO_CROPS: Crop[] = [];
const NO_IDS: string[] = [];
const NO_HIDDEN: ReadonlySet<string> = new Set();

/**
 * Легенда ярусов связи канона — из того же реестра, что рисует обводки
 * (`tierRegistry.ts`): ярус, добавленный туда, появляется и на полотне, и
 * здесь. Позиции без `key` — они поясняют обводку, а не значение заливки.
 */
const tierLegend = (): LegendEntry[] =>
  TIER_REGISTRY.map((entry) => ({
    label: tr(m.tier, { label: entry.label }),
    color: 'transparent',
    outline: `var(${entry.color})`,
    dashed: entry.dash !== null,
    note: entry.relationLabel,
  }));

/** Высоту кадра задаёт рамка: полотно заполняет её целиком. */
const CANVAS_FILL = { '--map-canvas-height': '100%' } as CSSProperties;

const TOOL_DEFAULTS: Record<MapMode, Required<MapTools>> = {
  full: { basemap: true, fullscreen: true, layerFilter: true, select: true },
  frame: { basemap: true, fullscreen: true, layerFilter: false, select: true },
  mini: { basemap: false, fullscreen: false, layerFilter: false, select: true },
};

/** Умолчания режима, поверх — то, что страница задала явно. */
export function resolveTools(mode: MapMode, tools?: MapTools): Required<MapTools> {
  return { ...TOOL_DEFAULTS[mode], ...(tools ?? {}) };
}

function frameClass(props: MapWidgetProps, expanded = false): string {
  const mode = props.mode ?? 'frame';
  // Панель слоёв занимает верхний левый угол: подпись кадра уходит вниз,
  // иначе две карточки садятся друг на друга.
  const tools = props.layersPanel && props.onColorByChange ? ' mapwidget--tools' : '';
  return `mapwidget mapwidget--${mode}${props.fill ? ' mapwidget--fill' : ''}${tools}${
    expanded ? ' mapwidget--expanded' : ''
  }`;
}

function frameStyle(props: MapWidgetProps): CSSProperties | undefined {
  if (props.fill) return undefined;
  const mode = props.mode ?? 'frame';
  return {
    height:
      props.height ??
      (mode === 'full' ? 'calc(100vh - var(--h-topbar) - 112px)' : mode === 'mini' ? 200 : 360),
  };
}

/** Форма фичи, которую понимает расчёт рамки канона. */
type GeoLike = { geometry: { [key: string]: unknown } | null };

/** Центр контура — по рамке его полигона: точке статуса большего не нужно. */
function centerOf(feature: GeoLike): LngLat | null {
  const box = geoBounds([feature]);
  return box ? [(box[0] + box[2]) / 2, (box[1] + box[3]) / 2] : null;
}

const str = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

function MapWidgetFrame(props: MapWidgetProps) {
  const t = useT();
  const {
    colorBy,
    legend = 'inline',
    filterIds,
    focusIds,
    bounds,
    mode = 'frame',
    layersPanel = false,
    layers,
    onColorByChange,
    onSelect,
    selectedId: selectedProp,
    card = 'auto',
    cardExtra,
    hatchIds,
    ringIds,
    ringLabel,
    ringDashed = false,
    workStatusById,
    quarantineById,
    data,
    points,
    timeline,
    crops,
    caption,
    layerUnavailable = null,
    resolveNames,
    panel,
  } = props;

  const tools = resolveTools(mode, props.tools);
  const [painted, setPainted] = useState(false);
  /** Рамка «показать наблюдение»: кнопка легенды переводит камеру. */
  const [watchFit, setWatchFit] = useState<Bbox | null>(null);
  const [expanded, setExpanded] = useState(false);
  /** Боковая панель управления в развёрнутом кадре свёрнута человеком. */
  const [panelClosed, setPanelClosed] = useState(false);
  /** Значения легенды, скрытые фильтром слоя. */
  const [hidden, setHidden] = useState<ReadonlySet<string>>(NO_HIDDEN);
  /** Контур в карточке (выбор внутри кадра). */
  const [cardId, setCardId] = useState<string | null>(null);

  const geo = useContourGeo();
  const analysis = useAnalysisContext();
  const cropList = crops ?? NO_CROPS;

  /* ------------------------------------------------- данные слоя */

  const layerData = useMemo<MapLayerData>(
    () => ({
      workStatusById: workStatusById ?? data?.workStatusById,
      quarantineById: quarantineById ?? data?.quarantineById,
      waterById: data?.waterById,
      values: data?.values,
    }),
    [workStatusById, quarantineById, data],
  );
  const spec = layerSpec(colorBy);
  const paint = useMemo(() => buildPaint(colorBy, cropList, layerData), [colorBy, cropList, layerData]);

  /*
   * Новые слои (вода, произвольные значения) без данных честно отказывают
   * сами. У прежних слоёв отказ называет страница (`layerUnavailable`):
   * их экраны знают причину точнее «данных нет».
   */
  const autoReason =
    (colorBy === 'water' || colorBy === 'values') && !layerHasData(colorBy, layerData)
      ? t(m.noLayerData, { title: paint.title })
      : null;
  const reason = layerUnavailable || autoReason;
  const suppressed = reason !== null && reason !== '';
  const fillColor = suppressed ? SUPPRESSED_FILL : paint.fillColor;

  // Смена слоя снимает фильтр значений: ключи другого слоя ничего не значат.
  useEffect(() => setHidden(NO_HIDDEN), [colorBy]);

  /** Фичи по id: центры точек, рамка фокуса, карточка. */
  const featuresById = useMemo(() => {
    const index = new Map<string, NonNullable<typeof geo.geo>['features'][number][]>();
    for (const feature of geo.geo?.features ?? []) {
      const id = String(feature.properties?.id ?? '');
      const list = index.get(id);
      if (list) list.push(feature);
      else index.set(id, [feature]);
    }
    return index;
  }, [geo.geo]);

  /** Свойства фич для слоёв: по одной записи на контур. */
  const featureProps = useMemo<LayerFeatureProps[]>(() => {
    const out: LayerFeatureProps[] = [];
    for (const [id, list] of featuresById) {
      out.push({ ...(list[0]?.properties as Record<string, unknown>), id });
    }
    return out;
  }, [featuresById]);

  const focusKey = (focusIds ?? []).join(',');
  const boundsKey = bounds ? bounds.join(',') : '';
  // Смена слоя или фокуса экрана снимает ручной перевод камеры на наблюдение.
  useEffect(() => setWatchFit(null), [colorBy, focusKey]);
  const fitBbox = useMemo<Bbox>(
    () =>
      resolveFitBbox({
        watchFit,
        focusIds: focusKey ? focusKey.split(',') : [],
        featuresById,
        pageBbox: boundsKey ? (boundsKey.split(',').map(Number) as Bbox) : null,
        districtBbox: geo.districtBbox,
      }),
    [watchFit, focusKey, boundsKey, featuresById, geo.districtBbox],
  );

  /* -------------------------------------------- точки статуса */

  const pointsMode: PointsMode = suppressed ? 'none' : (points ?? spec.points);

  const statusRows = useMemo(() => {
    if (pointsMode === 'none') return [];
    const allow = filterIds ? new Set(filterIds) : null;
    const rows: { props: LayerFeatureProps; center: [number, number] | null }[] = [];
    for (const props of featureProps) {
      if (allow && !allow.has(props.id)) continue;
      const meta = geo.byId.get(props.id);
      const feature = featuresById.get(props.id)?.[0];
      // Центр из реестра — если он настоящий: у канона поле бывает пустым
      // ([null, null]), и точка без координат молча не рисуется.
      const stated = meta?.center;
      const center =
        stated && Number.isFinite(stated[0]) && Number.isFinite(stated[1])
          ? ([stated[0], stated[1]] as [number, number])
          : feature
            ? (centerOf(feature as unknown as GeoLike) as [number, number] | null)
            : null;
      rows.push({ props, center });
    }
    return statusPoints(rows, spec, layerData, pointsMode, hidden);
  }, [pointsMode, filterIds, featureProps, geo.byId, featuresById, spec, layerData, hidden]);

  const watchPoints = useMemo<FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: statusRows.map((row) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: row.center },
        properties: { id: row.id, severity: row.severity },
      })),
    }),
    [statusRows],
  );

  /** Контуры, требующие внимания (критичные и предупреждения) — для кнопки «показать». */
  const attention = useMemo(
    () => statusRows.filter((row) => row.severity === 'critical' || row.severity === 'warning'),
    [statusRows],
  );

  /**
   * Сколько контуров под карантинным сигналом (красные и жёлтые) — то же определение,
   * что и таблица карантина. Точки ставятся только тем, чья геометрия уже
   * загружена, поэтому их может быть меньше — это сказано отдельно.
   */
  const watchTotal = useMemo(
    () =>
      colorBy === 'quarantine' && layerData.quarantineById
        ? Object.values(layerData.quarantineById).filter((s) => s !== 'green').length
        : 0,
    [colorBy, layerData.quarantineById],
  );

  const showAttention = useCallback(() => {
    if (attention.length === 0) return;
    const box: Bbox = [Infinity, Infinity, -Infinity, -Infinity];
    for (const { center } of attention) {
      box[0] = Math.min(box[0], center[0]);
      box[1] = Math.min(box[1], center[1]);
      box[2] = Math.max(box[2], center[0]);
      box[3] = Math.max(box[3], center[1]);
    }
    setWatchFit(box);
  }, [attention]);

  /* --------------------------------------------------- легенда по факту */

  /**
   * Значения, реально встреченные в переданных данных. Легенда, показывающая
   * культуру, которой в выборке нет, врёт про район не меньше пустой карты.
   */
  const presentKeys = useMemo<Set<string> | null>(() => {
    if (suppressed || featureProps.length === 0) return null;
    if (!layerHasData(colorBy, layerData)) return null;
    return presentValues(featureProps, spec, layerData, filterIds ?? null);
  }, [suppressed, featureProps, colorBy, spec, layerData, filterIds]);

  const showTiers = colorBy === 'geometryStatus' && geo.origin === 'api';
  const legendEntries = useMemo<LegendEntry[]>(() => {
    const tiers = showTiers ? tierLegend() : [];
    const kept = presentKeys
      ? paint.legend.filter((entry) => entry.key === undefined || presentKeys.has(entry.key))
      : paint.legend;
    // Точка в образце — ровно там, где карта её ставит (правило слоя и режим).
    const marked = (kept.length > 0 ? kept : paint.legend).map((entry) => {
      if (entry.key === undefined) return entry;
      const severity = spec.severity(entry.key, layerData);
      return { ...entry, dot: wantsPoint(severity, pointsMode) ? SEVERITY_COLOR[severity] : undefined };
    });
    const ring: LegendEntry[] =
      ringLabel && ringIds && ringIds.length > 0
        ? [{ label: ringLabel, color: 'transparent', outline: PALETTE.ink, dashed: ringDashed }]
        : [];
    return [...marked, ...ring, ...tiers];
  }, [paint.legend, presentKeys, showTiers, spec, layerData, pointsMode, ringLabel, ringIds, ringDashed]);

  const toggleHidden = useCallback((key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /** Фильтр полотна: страница сужает выборку, фильтр слоя прячет значения. */
  const canvasFilter = useMemo(
    () => visibleIds(featureProps, spec, layerData, hidden, filterIds ?? null),
    [featureProps, spec, layerData, hidden, filterIds],
  );

  /* ------------------------------------------------- выбор и карточка */

  const byId = geo.byId;
  const selectable = tools.select;
  const cardMode = card === 'auto' ? (mode === 'full' || expanded ? 'side' : 'drawer') : card;
  // Шторка дизайн-системы живёт поверх приложения; в развёрнутом кадре
  // карточка — панель внутри кадра, чтобы не прятаться за ним.
  const cardVariant = cardMode === 'drawer' && expanded ? 'side' : cardMode;

  const select = useCallback(
    (id: string | null) => {
      if (!selectable) return;
      if (cardMode !== 'none') setCardId(id);
      onSelect?.(id ? byId.get(id) ?? null : null);
    },
    [selectable, cardMode, onSelect, byId],
  );

  const selectedId = selectedProp ?? cardId;

  // Имена для карточки: страница может дать резолверы; иначе — справочники
  // среза, которые читаются только когда карточка открыта (кэш общий).
  const wantNames = cardId !== null;
  const farmsFile = useTrackData<Farm[]>(wantNames && !resolveNames?.farm ? FILES.farms : null);
  const massifsFile = useTrackData<Massif[]>(wantNames && !resolveNames?.massif ? FILES.massifs : null);
  const cropsFile = useTrackData<Crop[]>(wantNames && cropList.length === 0 ? FILES.crops : null);

  const cardData = useMemo<ContourCardData | null>(() => {
    if (!cardId) return null;
    const meta = byId.get(cardId) ?? null;
    const props = (featuresById.get(cardId)?.[0]?.properties ?? {}) as Record<string, unknown>;
    const farmId = meta?.farm_id ?? str(props.farm_id);
    const massifId = meta?.massif_id ?? str(props.massif_id);
    const cropId = meta?.crop_2026 ?? str(props.crop_2026);
    const cropRows = cropList.length > 0 ? cropList : (cropsFile.data ?? []);
    const farmName = farmId
      ? (resolveNames?.farm?.(farmId) ?? farmsFile.data?.find((row) => row.id === farmId)?.name ?? null)
      : null;
    const massifName = massifId
      ? (resolveNames?.massif?.(massifId) ??
        massifsFile.data?.find((row) => row.id === massifId)?.name ??
        null)
      : null;
    const area =
      meta && meta.area_stated !== false
        ? meta.area_ha
        : typeof props.area_ha === 'number'
          ? props.area_ha
          : null;
    const key = spec.valueOf({ ...props, id: cardId }, layerData);
    const entry = suppressed ? null : (paint.legend.find((row) => row.key === key) ?? null);
    return {
      id: cardId,
      no: meta?.no ?? str(props.no) ?? cardId,
      meta,
      areaHa: area,
      cropName: cropId ? (cropRows.find((row) => row.id === cropId)?.name ?? cropId) : null,
      farmId,
      farmName,
      massifId,
      massifName,
      landUser: str(props.land_user),
      landUsersCount:
        meta?.land_users_count ?? (typeof props.land_users_count === 'number' ? props.land_users_count : null),
      layerTitle: paint.title,
      layerEntry: entry,
      // Карточка контура открывается на вкладке текущего слоя: из слоя
      // карантина — сразу карантинная история (agroai-6al.34).
      contourTab: CARD_TAB[colorBy],
    };
  }, [
    colorBy,
    cardId,
    byId,
    featuresById,
    cropList,
    cropsFile.data,
    farmsFile.data,
    massifsFile.data,
    resolveNames,
    spec,
    layerData,
    suppressed,
    paint,
  ]);

  /* ----------------------------------------------- развернуть кадр */

  useEffect(() => {
    if (!expanded) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && cardId === null) setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    // Страница под развёрнутым кадром не прокручивается.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [expanded, cardId]);

  /* ------------------------------------------------------------- полотно */

  const renderTip = useCallback(
    (tip: AstraFeatureProps) => {
      const farmId = tip.farm_id ? String(tip.farm_id) : null;
      const massifId = tip.massif_id ? String(tip.massif_id) : null;
      const farmName = farmId ? resolveNames?.farm?.(farmId) : null;
      const massifName = massifId ? resolveNames?.massif?.(massifId) : null;
      // Землепользователь из слоя канона — если атрибутов среза у фичи нет.
      const landUser = typeof tip.land_user === 'string' ? tip.land_user : null;
      const area = typeof tip.area_ha === 'number' ? tip.area_ha : null;
      return (
        <div className="maptip-body">
          <strong>{t(m.tipNo, { no: tip.no ? String(tip.no) : tip.id })}</strong>
          <div>
            {farmName ??
              landUser ??
              (farmId ? t(m.tipFarmNo, { id: farmId }) : t(m.tipNoFarm))}
          </div>
          {massifId ? <div>{massifName ?? t(m.tipMassifNo, { id: massifId })}</div> : null}
          <div className="num">{haUnit(area)}</div>
        </div>
      );
    },
    [resolveNames, t],
  );

  const stateById = spec.stateKey === 'v' && !suppressed ? spec.stateById?.(layerData) : undefined;

  const astra = useMemo(
    () => ({
      fillColor,
      pending: colorBy === 'workStatus' && !suppressed,
      candidate: colorBy === 'geometryStatus',
      watch: colorBy === 'quarantine' && !suppressed,
      points: pointsMode !== 'none',
      filterIds: canvasFilter,
      hatchIds: hatchIds ?? NO_IDS,
      ringIds: ringIds ?? NO_IDS,
      ringDashed,
      selectedId: selectedId ?? null,
      workStatusById: layerData.workStatusById,
      quarantineById: layerData.quarantineById,
      valueById: stateById,
      watchPoints,
      // Ярусы связи — сведение о геометрии: показываются на слое «Геометрия»
      // и только у канонической выдачи (у фикстур прототипа ярусов нет).
      tiers: showTiers,
      renderTip,
      onSelect: select,
      onPainted: () => setPainted(true),
    }),
    [
      fillColor,
      colorBy,
      suppressed,
      pointsMode,
      canvasFilter,
      hatchIds,
      ringIds,
      ringDashed,
      selectedId,
      layerData.workStatusById,
      layerData.quarantineById,
      stateById,
      watchPoints,
      showTiers,
      renderTip,
      select,
    ],
  );

  /* ------------------------------------------------------------- рендер */

  if (geo.state === 'missing') {
    return (
      <div className={frameClass(props)} style={frameStyle(props)}>
        <EmptyState
          title={t(m.missingTitle)}
          reason={
            geo.origin === 'api'
              ? t(m.missingApi, { reason: geo.reason ?? t(m.geoNoReason) })
              : t(m.missingFixtures, { reason: geo.reason ?? t(m.geoNoReason) })
          }
        />
      </div>
    );
  }

  const legendPlace = expanded && legend === 'below' ? 'panel' : legend;
  // Панель управления — только в развёрнутом кадре: на странице тот же
  // интерфейс стоит рядом с картой сам.
  const showPanel = expanded && panel !== undefined;
  const attentionAction =
    attention.length > 0 && !suppressed ? (
      <button type="button" className="maplegend__btn focus-ring" onClick={showAttention}>
        {colorBy === 'quarantine'
          ? attention.length < watchTotal
            ? t(m.watchShowOnMap, { count: watchTotal, onMap: attention.length })
            : t(m.watchShow, { count: watchTotal })
          : t(m.attentionShow, { count: attention.length })}
      </button>
    ) : null;

  const legendContent = suppressed ? (
    <MapLayerNotice title={paint.title} reason={reason ?? ''} />
  ) : (
    <MapLegend
      // Новый слой — новая легенда: свёрнутость и фильтр не переезжают.
      key={colorBy}
      title={paint.title}
      entries={legendEntries}
      action={attentionAction}
      collapsible={legendPlace !== 'below'}
      defaultCollapsed={legendPlace === 'collapsed' || mode === 'mini'}
      hidden={tools.layerFilter ? hidden : undefined}
      onToggle={tools.layerFilter ? toggleHidden : undefined}
    />
  );

  const hiddenNote =
    hidden.size > 0 ? (
      <div className="mapwidget__filtered" role="status">
        {t(m.hiddenCount, { count: hidden.size })}{' '}
        <button type="button" className="mapwidget__linkbtn focus-ring" onClick={() => setHidden(NO_HIDDEN)}>
          {t(m.showAll)}
        </button>
      </div>
    ) : null;

  const figure = (
    <figure className={frameClass(props, expanded)} style={expanded ? undefined : frameStyle(props)}>
      <div className="mapwidget__body">
      {showPanel && panel ? (
        <aside
          className={`mapwidget__panel${panelClosed ? ' is-closed' : ''}`}
          aria-label={panel.title}
        >
          <button
            type="button"
            className="mapwidget__panel-toggle focus-ring"
            aria-expanded={!panelClosed}
            aria-label={panelClosed ? t(m.panelShow, { title: panel.title }) : t(m.panelHide, { title: panel.title })}
            title={panelClosed ? t(m.panelShow, { title: panel.title }) : t(m.panelHide, { title: panel.title })}
            onClick={() => setPanelClosed((value) => !value)}
          >
            <Icon icon={panelClosed ? PanelLeftOpen : PanelLeftClose} size={16} />
          </button>
          {panelClosed ? null : (
            <>
              <div className="mapwidget__panel-head">
                <strong className="mapwidget__panel-title">{panel.title}</strong>
                {panel.summary ? <span className="mapwidget__panel-summary">{panel.summary}</span> : null}
              </div>
              <div className="mapwidget__panel-body">{panel.content}</div>
            </>
          )}
        </aside>
      ) : null}
      <div className="mapwidget__stage">
        <div className="mapwidget__canvas" style={CANVAS_FILL}>
          {geo.state === 'ready' && geo.geo ? (
            <MapCanvas
              district={geo.district as never}
              mahallas={geo.mahallas as never}
              contours={geo.geo as never}
              fitBbox={fitBbox}
              palette={[]}
              cropPalette={[]}
              thematic="massif"
              massifName={() => null}
              onViewChange={() => undefined}
              onPick={() => undefined}
              interactive={mode !== 'mini'}
              basemapSwitch={tools.basemap}
              astra={astra}
            />
          ) : null}
        </div>

        {caption ? <figcaption className={`mapwidget__caption${legendPlace !== 'panel' && legendPlace !== 'below' ? ' mapwidget__caption--legend-left' : ''}`}>{caption}</figcaption> : null}

        {!painted ? (
          <div className="mapwidget__skeleton">
            <span className="mapwidget__skeleton-note">
              {geo.state === 'loading'
                ? t(m.loadingGeo)
                : t(m.buildingFrame)}
            </span>
          </div>
        ) : null}

        {geo.truncated || (geo.state === 'ready' && geo.unmatched > 0) ? (
          <div className="mapwidget__veil">
            {[
              geo.truncated,
              geo.unmatched > 0
                ? geo.reason
                  ? t(m.unmatchedReason, { count: geo.unmatched, reason: geo.reason })
                  : t(m.unmatched, { count: geo.unmatched })
                : null,
            ]
              .filter(Boolean)
              .join(' ')}
          </div>
        ) : null}

        {layersPanel && onColorByChange ? (
          <div className="mapwidget__tl">
            <LayersPanel
              value={colorBy}
              onChange={onColorByChange}
              options={layers}
              valuesTitle={layerData.values?.title}
            />
          </div>
        ) : null}

        {legendPlace !== 'below' && (
          <div className={legendPlace === 'panel' ? 'mapwidget__br' : 'mapwidget__bl'}>
            <div className="mapwidget__legendstack">
              {hiddenNote}
              {legendContent}
            </div>
          </div>
        )}

        <div className={`mapwidget__toolbar${mode === 'mini' ? ' is-bare' : ''}`}>
          {geo.source === 'stub' ? <EvidenceMark kind="demo" label={t(m.stubGeometry)} /> : null}
          {tools.fullscreen ? (
            <button
              type="button"
              className="mapwidget__toolbtn focus-ring"
              aria-pressed={expanded}
              aria-label={expanded ? t(m.collapse) : t(m.expand)}
              title={expanded ? t(m.collapseTitle) : t(m.expandTitle)}
              onClick={() => setExpanded((value) => !value)}
            >
              <Icon icon={expanded ? Minimize2 : Maximize2} size={16} />
            </button>
          ) : null}
        </div>

        {cardData && cardVariant === 'side' ? (
          <ContourCard
            data={cardData}
            variant="side"
            onClose={() => setCardId(null)}
            linkSearch={analysis.linkSearch()}
            extra={cardExtra?.(cardData)}
          />
        ) : null}
      </div>
      </div>

      {timeline ? (
        <div className="mapwidget__timeline">
          <MapTimeline {...timeline} />
        </div>
      ) : null}
    </figure>
  );

  return (
    <>
      {expanded ? <div className="mapwidget__placeholder" style={frameStyle(props)} aria-hidden="true" /> : null}
      {figure}
      {legendPlace === 'below' && (
        <div className="mapwidget__legend-below">
          {hiddenNote}
          {legendContent}
        </div>
      )}
      {cardData && cardVariant === 'drawer' ? (
        <ContourCard
          data={cardData}
          variant="drawer"
          onClose={() => setCardId(null)}
          linkSearch={analysis.linkSearch()}
          extra={cardExtra?.(cardData)}
        />
      ) : null}
    </>
  );
}

/**
 * Публичный контракт: граница ошибок надета снаружи кадра, иначе исключение
 * внутри карты уносило бы всё приложение в белый экран.
 */
export function MapWidget(props: MapWidgetProps) {
  return (
    <MapErrorBoundary className={frameClass(props)} style={frameStyle(props)}>
      <MapWidgetFrame {...props} />
    </MapErrorBoundary>
  );
}

/** Имя единой карты в протоколе (`docs/rules/map-widget.md`). */
/** Вкладка карточки контура по слою карты (`?tab=`). */
const CARD_TAB: Partial<Record<ColorBy, string>> = {
  quarantine: 'quarantine',
  water: 'water',
  workStatus: 'works',
};

export const AgroMap = MapWidget;
export type AgroMapProps = MapWidgetProps;

export { COLOR_BY_LABEL, LEGEND_NONE };
export type { ContourCardData } from './ContourCard';
export type { MapLayerData, PointsMode, ValuesLayer, WaterStateValue, DataTone } from './layers';
export type { MapTimelineProps } from './MapTimeline';

export default MapWidget;
